import { Component, Input, OnChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  BmExpGroupFg, BmMgmFeeFg,
  BmMgmFeeGroupForm,
  BmMgmFeePcntForm,
  BmItemForm,
  BudgetModelFormGroup,
} from "../../pojo/bm-form";
import { NgForOf } from "@angular/common";
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { FormArray, FormGroup } from "@angular/forms";
import { BmExpGroup, BmMgmFeeGroup, BmPeriodEnum } from "../../pojo/bm-classes";
import { BmDefaultFormCreatorService } from "../../services/general/bm-default-form-creator.service";
import { values } from "lodash";
import { BmSubscriptionsService } from "../../services/general/subscription/bm-subscriptions.service";
import { BmCalcType, BmDataFillingType, BmGroupAction, BmGroupType } from "../../pojo/bm-enums";
import { BmExpensesCalcService } from "../../services/expenses/bm-expenses-calc.service";
import { GroupActionHelperService } from "./service/group-action-helper.service";
import { BmCalcHelperService } from "../../services/general/bm-calc-helper.service";
import { BmGrandTotalMgmFeeCalcService } from "../../services/mgmFee/total/bm-grand-total-mgm-fee-calc.service";
import { BmMgmGeneralCalcService } from "../../services/mgmFee/general/bm-mgm-general-calc.service";
import {
  TableGroupIconComponent,
} from "../../../shared/component/icons-deprecated/table-group-icon/table-group-icon.component";
import { OnHoverDirective } from "../../../../util/directives/on-hover-directive";

@Component({
  selector: 'app-table-budget-model',
  templateUrl: './table-budget-model.component.html',
  styleUrls: ['./table-budget-model.component.scss'],
  standalone: true,
  imports: [
    NgForOf,
    SharedComponentsModule,
    TableGroupIconComponent,
    OnHoverDirective,
  ],
})
export class TableBudgetModelComponent implements OnChanges {
  @Input() budgetForm: BudgetModelFormGroup;

  isAllQuartersCollapsed = false;
  collapsedYears: number[] = [];
  dataFillingTypes = Object.values(BmDataFillingType);

  protected readonly BmPeriodEnum = BmPeriodEnum;
  protected readonly BmGroupAction = BmGroupAction;
  protected readonly BmDataFillingType = BmDataFillingType;
  protected readonly BmGroupType = BmGroupType;
  protected readonly values = values;

  ngOnChanges():void {
    this.isAllQuartersCollapsed = false;
    this.collapsedYears = [];
  }

  /**
   * MANAGEMENT FEE
   *
   * Handle actions ADD, DELETE, DUPLICATE, RENAME in separate service
   * @param action type of action
   * @param groupFG selected FormGroup
   */
  onMgmFeeGroupAction(action: BmGroupAction, groupFG?: FormGroup<BmMgmFeeGroupForm>): void {
    const groupsFA = this.budgetForm.controls.modelData.controls.mgmFee.controls.groups;
    const mgmFee = groupsFA.parent as FormGroup<BmMgmFeeFg>;

    GroupActionHelperService.handleGroupAction(
      action,
      groupsFA,
      groupFG,
      (name: string, priority: number): BmMgmFeeGroup =>
        BmDefaultFormCreatorService.createDefaultMgmFeeGroup(name, this.budgetForm.controls.yearsNumber.value, priority),
      (group: BmMgmFeeGroup): FormGroup<BmMgmFeeGroupForm> =>
        BmMgmGeneralCalcService.createMgmFeeGroupForm(group, this.budgetForm.controls.yearsNumber.value),
      (fg) => BmSubscriptionsService.subscribeToMgmFeeGroup(fg),
      'Management fee group'
    );
    BmGrandTotalMgmFeeCalcService.recalculateGrandTotal(mgmFee);
  }

  /**
   * OPERATIONAL EXPENSES
   *
   * Handle actions ADD, DELETE, DUPLICATE, RENAME in separate service
   * @param action type of action
   * @param groupFG selected FormGroup
   */
  onOpExpGroupAction(action: BmGroupAction, groupFG?: FormGroup<BmExpGroupFg>): void {
    const groupsFA = this.budgetForm.controls.modelData.controls.operatingExpenses.controls.groups;

    GroupActionHelperService.handleGroupAction(
      action,
      groupsFA,
      groupFG,
      (name: string, priority: number): BmExpGroup =>
        BmDefaultFormCreatorService.createExpensesGroup(name, 0, BmDataFillingType.MANUAL, this.budgetForm.controls.yearsNumber.value, priority),
      (model: BmExpGroup): FormGroup<BmExpGroupFg> =>
        BmExpensesCalcService.createExpGroupForm(model),
      (fg) => BmSubscriptionsService.subscribeToOpExpGroup(fg),
      'Operating expenses group'
    );
    BmExpensesCalcService.recalculateSectionTotal(this.budgetForm.controls.modelData.controls.operatingExpenses);
  }

  /**
   * ORGANIZATIONAL EXPENSES
   *
   * Handle actions ADD, DELETE, DUPLICATE, RENAME in separate service
   * @param action type of action
   * @param groupFG selected FormGroup
   */
  onOrgExpGroupAction(action: BmGroupAction, groupFG?: FormGroup<BmExpGroupFg>): void {
    const groupsFA = this.budgetForm.controls.modelData.controls.organizationalExpenses.controls.groups;

    GroupActionHelperService.handleGroupAction(
      action,
      groupsFA,
      groupFG,
      (name: string, priority: number): BmExpGroup =>
        BmDefaultFormCreatorService.createExpensesGroup(name, 0, BmDataFillingType.MANUAL, this.budgetForm.controls.yearsNumber.value, priority),
      (model: BmExpGroup): FormGroup<BmExpGroupFg> =>
        BmExpensesCalcService.createExpGroupForm(model),
      (fg: FormGroup<BmExpGroupFg>): void =>
        BmSubscriptionsService.subscribeToOrgExpGroup(fg),
      'Organizational expenses group'
    );
    BmExpensesCalcService.recalculateSectionTotal(this.budgetForm.controls.modelData.controls.organizationalExpenses);
  }

  /**
   * DRAG DROP - OPERATING EXPENSES or ORGANIZATIONAL EXPENSES based on groupsFA
   * Handle drag and drop reordering of expenses groups
   */
  changeOrderInExpensesGroup(event: CdkDragDrop<string[]>, groupsFA: FormArray<FormGroup<BmExpGroupFg>>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    // Move item in form array
    moveItemInArray(groupsFA.controls, event.previousIndex, event.currentIndex);

    // Update priority for each group after reorder
    groupsFA.controls.forEach((group, index) => {
      group.patchValue({ priority: index });
    });
  }

  /**
   * Handle edit item action (clicking on edit.svg in table sell)
   *
   * (EDIT)   Editable value was DISABLED => Make ENABLED and with calcType MANUAL
   * (CANCEL) Editable value was ENABLE => Make DISABLE and with calcType GENERATED
   *
   * @param item selected control
   * @param groupType needed to specify the group with unique logic
   */
  editItemValue(item: FormGroup<BmItemForm>, groupType?: BmGroupType): void {
    const itemsFa = item.parent as FormArray<FormGroup<BmItemForm>>;

    if (item.controls.itemValue.disabled) {
      item.controls.itemValue.enable({ emitEvent: false });
      item.controls.calcType.patchValue(BmCalcType.MANUAL, { emitEvent: false });
    } else {
      item.controls.itemValue.disable({ emitEvent: false });
      let previousValue: number;
      const formGroup = itemsFa.parent as FormGroup<any>; // could be various FormGroups
      const standardValue = formGroup.value.standardValue as number;

      switch (groupType) {
        case BmGroupType.MGM_FEE_PCNT:
          const formGroupMgmFee = itemsFa.parent as FormGroup<BmMgmFeePcntForm>;
          const standardValueMgmFee = formGroupMgmFee.value.standardValue;

          if (item.getRawValue().period.type === BmPeriodEnum.YEAR) {
            previousValue = standardValueMgmFee;
          }
          if (item.getRawValue().period.type === BmPeriodEnum.QUARTER) {
            previousValue = standardValueMgmFee / 4;
          }
          break;
        case BmGroupType.EXPENSES:
          previousValue = this.handleExpensesItemChange(itemsFa, item);
          break;
        case BmGroupType.INVESTMENT_VALUE:
          previousValue = 0;
          break;
        case BmGroupType.INVESTMENT_PCNT:
          previousValue = 0;
          break;
        default:
          previousValue = standardValue;
          break;
      }
      item.controls.calcType.patchValue(BmCalcType.GENERATED, { emitEvent: false });
      item.controls.itemValue.markAsDirty(); // mark as dirty = mark as changed by user in UI => needed to update values in service
      item.controls.itemValue.patchValue(previousValue);
    }
  }

  toggleQuartersInSelectedYear(yearIdx: number): void {
    const shouldCollapse = !this.collapsedYears.includes(yearIdx);

    this.budgetForm.controls.modelHeaders.controls
      .filter((header) => header.value.yearNumber === yearIdx)
      .forEach((header) => header.controls.isCollapsed.patchValue(!header.controls.isCollapsed.value, { emitEvent: false }));

    this.collapsedYears = shouldCollapse ? [...this.collapsedYears, yearIdx].sort((a, b) => a - b) : this.collapsedYears.filter((y) => y !== yearIdx);
  }

  toggleAllQuarters(): void {
    const years = Number(this.budgetForm.controls.yearsNumber.value) || 0;

    /* update every QUARTER cell; year-cells get the same flag if you wish */
    this.budgetForm.controls.modelHeaders.controls.forEach((header) =>
      header.controls.isCollapsed.patchValue(this.isAllQuartersCollapsed, { emitEvent: false })
    );

    /* sync the helper array */
    this.collapsedYears = this.isAllQuartersCollapsed ? Array.from({ length: years }, (_, i) => i + 1) : [];
  }

  /**
   * Helper method to handle previous value calculation on 'x' icon click in Expenses group
   * Based on dataFillingType and standardValueExpenses will be previous value selected
   * @param itemsFa array of group Items
   * @param item selected Item
   */
  private handleExpensesItemChange(itemsFa:FormArray<FormGroup<BmItemForm>>, item:FormGroup<BmItemForm>): number {
    const formGroupExpenses = itemsFa.parent as FormGroup<BmExpGroupFg>;
    const standardValueExpenses = formGroupExpenses.value.standardValue;
    const isYearItem = item.getRawValue().period.type === BmPeriodEnum.YEAR;
    let previousValue: number;

    switch (formGroupExpenses.value.dataFillingType) {
      case BmDataFillingType.YEARLY:
        previousValue = isYearItem ? standardValueExpenses : (standardValueExpenses / 4);
        break;
      case BmDataFillingType.QUARTERLY:
        previousValue = isYearItem ? (standardValueExpenses * 4) : standardValueExpenses;
        break;
      case BmDataFillingType.MONTHLY:
        previousValue = isYearItem ? (standardValueExpenses * 12) : (standardValueExpenses * 3);
        break;
      case BmDataFillingType.MANUAL:
        if (isYearItem) {
          const quartersFG = BmCalcHelperService.findQuarterItemsInSelectedYear(formGroupExpenses.controls.items, item.value.period.yearNumber);
          previousValue = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
        } else {
          previousValue = 0;
        }
        break;
    }
    return previousValue;
  }
}
